import { Directive, ElementRef, HostListener, Renderer2, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * ControlValueAccessor for native <input type="date"> that keeps the bound
 * form/model value as a JavaScript Date (matching what the app's TS and
 * service payloads expect), while the DOM element uses the required
 * yyyy-MM-dd string representation.
 *
 * Applied automatically to every native date input, so migrating a
 * mat-datepicker to <input type="date"> needs no component TS changes.
 */
@Directive({
  selector: 'input[type=date]',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NativeDateValueAccessor),
      multi: true,
    },
  ],
})
export class NativeDateValueAccessor implements ControlValueAccessor {
  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2
  ) {}

  @HostListener('input', ['$event.target.value'])
  handleInput(value: string): void {
    this.onChange(value ? this.toDate(value) : null);
  }

  @HostListener('blur')
  handleBlur(): void {
    this.onTouched();
  }

  writeValue(value: any): void {
    this.renderer.setProperty(this.el.nativeElement, 'value', this.toInputString(value));
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  /** Date | moment | ISO string -> "yyyy-MM-dd" (or "" when empty/invalid). */
  private toInputString(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    let date: Date;
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'object' && typeof value.toDate === 'function') {
      // moment object (legacy values coming through MomentDateAdapter)
      date = value.toDate();
    } else {
      date = new Date(value);
    }
    if (isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /** "yyyy-MM-dd" -> local Date at midnight (avoids the UTC-parse day shift). */
  private toDate(value: string): Date | null {
    const parts = value.split('-').map((part) => parseInt(part, 10));
    if (parts.length !== 3 || parts.some(isNaN)) {
      return null;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }
}
